import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Zap } from "lucide-react";

const NAV_LINKS = [
  { label: "Lösungen", href: "/loesungen" },
  { label: "Leistungen", href: "/leistungen" },
  { label: "Über uns", href: "/ueber-uns" },
  { label: "Kontakt", href: "/kontakt" },
];

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050911]/95 backdrop-blur-md border-b border-white/5 shadow-lg shadow-black/30" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-lg bg-cyan-400 flex items-center justify-center shadow-lg shadow-cyan-400/25 group-hover:shadow-cyan-400/40 transition-shadow">
            <Zap size={16} className="text-slate-900 fill-slate-900" />
          </div>
          <span className="font-bold text-lg tracking-tight text-white">
            signal<span className="text-cyan-400">Solution</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium ${
                location.pathname === l.href
                  ? "text-cyan-400 bg-cyan-400/10"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* CTA buttons */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm text-white/50 hover:text-white/80 transition-colors px-3 py-2"
          >
            Login
          </Link>
          <Link
            to="/kontakt#demo-form"
            className="px-5 py-2 bg-cyan-400 text-slate-900 text-sm font-semibold rounded-lg hover:bg-cyan-300 transition-all shadow-lg shadow-cyan-400/20 hover:shadow-cyan-400/30"
          >
            Demo anfragen
          </Link>
        </div>

        {/* Mobile: kompakter CTA + Hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <Link
            to="/kontakt#demo-form"
            className="px-3 py-1.5 bg-cyan-400 text-slate-900 text-xs font-semibold rounded-md hover:bg-cyan-300 transition-all shadow-md shadow-cyan-400/20 whitespace-nowrap"
            data-testid="mobile-cta-btn"
          >
            Demo anfragen
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="text-white/70 hover:text-white transition-colors p-2"
            aria-label="Menü"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-[#050911]/98 backdrop-blur-md border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === l.href
                    ? "text-cyan-400 bg-cyan-400/10"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-white/5 flex flex-col gap-2">
              <Link to="/login" className="block px-4 py-3 text-sm text-white/50 hover:text-white text-center">
                Login
              </Link>
              <Link
                to="/kontakt#demo-form"
                className="block px-5 py-3 bg-cyan-400 text-slate-900 text-sm font-semibold rounded-lg text-center hover:bg-cyan-300 transition-all"
              >
                Demo anfragen
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-[#030710] border-t border-white/5 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-cyan-400 flex items-center justify-center">
                <Zap size={16} className="text-slate-900 fill-slate-900" />
              </div>
              <span className="font-bold text-lg text-white">
                signal<span className="text-cyan-400">Solution</span>
              </span>
            </Link>
            <p className="text-sm text-white/40 leading-relaxed">
              Professionelle Websites für lokale Unternehmen. Schnell, modern und wirkungsvoll.
            </p>
          </div>

          {/* Links */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/30 mb-4">Produkt</p>
            <ul className="space-y-2.5">
              {[["Lösungen", "/loesungen"], ["Leistungen", "/leistungen"]].map(([l, h]) => (
                <li key={h}><Link to={h} className="text-sm text-white/50 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/30 mb-4">Unternehmen</p>
            <ul className="space-y-2.5">
              {[["Über uns", "/ueber-uns"], ["Kontakt", "/kontakt"]].map(([l, h]) => (
                <li key={h}><Link to={h} className="text-sm text-white/50 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/30 mb-4">Kontakt</p>
            <ul className="space-y-2.5">
              <li>
                <Link to="/kontakt" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
                  Demo anfragen →
                </Link>
              </li>
              <li className="text-sm text-white/50">Antwort innerhalb 24h</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/25">
            © {new Date().getFullYear()} signalSolution. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-white/25 hover:text-white/50 transition-colors">Impressum</a>
            <a href="#" className="text-xs text-white/25 hover:text-white/50 transition-colors">Datenschutz</a>
            <a href="#" className="text-xs text-white/25 hover:text-white/50 transition-colors">AGB</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#050911] text-white">
      <PublicNav />
      <main>{children}</main>
      <PublicFooter />
    </div>
  );
}
